import React from 'react';
import type { ProductImage } from '@vastra/types';

/**
 * Renders a product image using `<picture>` with AVIF → WebP source order
 * and a `srcset` that lets the browser pick the smallest variant that still
 * fills the slot cleanly on the user's device.
 *
 * Variants we rely on (produced by the API image pipeline):
 *   - thumbnail ~300w (admin lists, tiny cards, mini-cart)
 *   - listing   ~600w (product cards in grids)
 *   - main     ~1200w (PDP hero, full-bleed)
 *
 * The `variant` prop picks which variant to use as the `src` fallback for
 * browsers that don't understand `<picture>`/srcset at all (rare, but
 * keeps the request small when it happens).
 *
 * `sizes` defaults to storefront product-card grids: full-width on phone,
 * half on tablet, ~1/3 on desktop (3-col). Override for denser 4-col home
 * rows or PDP heroes.
 */
export interface ResponsiveImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes'> {
  image: Pick<
    ProductImage,
    | 'url'
    | 'listingUrl'
    | 'thumbnailUrl'
    | 'avifUrl'
    | 'avifListingUrl'
    | 'avifThumbnailUrl'
    | 'alt'
  >;
  /** Picks which variant is the src fallback. Default `'listing'`. */
  variant?: 'main' | 'listing' | 'thumbnail';
  /** Override srcset `sizes`. Default targets a 3-col desktop / 2-col tablet grid. */
  sizes?: string;
  className?: string;
}

/** Matches ProductListing (`sm:2` / `xl:3`) so the browser prefers listing (~600w) over thumb (~300w). */
const DEFAULT_SIZES = '(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw';

function joinSrcset(parts: { url?: string; width: number }[]): string | undefined {
  const valid = parts.filter((p): p is { url: string; width: number } => Boolean(p.url));
  if (valid.length === 0) return undefined;
  return valid.map((p) => `${p.url} ${p.width}w`).join(', ');
}

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  image,
  variant = 'listing',
  sizes = DEFAULT_SIZES,
  alt,
  className,
  ...rest
}) => {
  const webpSrcSet = joinSrcset([
    { url: image.thumbnailUrl, width: 300 },
    { url: image.listingUrl, width: 600 },
    { url: image.url, width: 1200 },
  ]);
  const avifSrcSet = joinSrcset([
    { url: image.avifThumbnailUrl, width: 300 },
    { url: image.avifListingUrl, width: 600 },
    { url: image.avifUrl, width: 1200 },
  ]);

  const fallbackSrc =
    (variant === 'thumbnail' && image.thumbnailUrl) ||
    (variant === 'listing' && image.listingUrl) ||
    image.url;

  return (
    <picture className="contents">
      {avifSrcSet && <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />}
      {webpSrcSet && <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />}
      <img
        src={fallbackSrc}
        alt={alt ?? image.alt ?? ''}
        className={className}
        {...rest}
      />
    </picture>
  );
};

export default ResponsiveImage;
